import { ExcelColumn } from "api/generated/lumen";
import Claims from "features/auth/claims";
import useAuth from "features/auth/useAuth";
import useFeature from "hooks/useFeature";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface UseFailuresListColumnTitles {
  columnTitles: string[];
  exportColumnTitles: ExcelColumn[];
}

const useFailuresListColumnTitles = (): UseFailuresListColumnTitles => {
  const { t } = useTranslation();
  const { hasClaim } = useAuth();

  const { isEnabled: isMaintainedDevicesEnabled } =
    useFeature("maintainedDevices");

  const { isEnabled: isContractorAcceptOfTaskAssignment } = useFeature(
    "contractorAcceptOfTaskAssignment"
  );

  const { showInMasterView: isFailureListParam1ShowInMasterView } =
    useFeature("failureListParam1");

  const { showInMasterView: isFailureListParam2ShowInMasterView } =
    useFeature("failureListParam2");

  const { label: failureListParam1Label } = useFeature("failureListParam1");
  const { label: failureListParam2Label } = useFeature("failureListParam1");

  const columnTitles = useMemo(
    () => [
      t("failure.id"),
      ...(isMaintainedDevicesEnabled
        ? [t("failure.device")]
        : [
            t("failure.zip"),
            t("failure.town"),
            t("failure.street"),
            t("failure.houseNumber"),
          ]),
      t("failure.type"),
      t("failure.commentColumnTitle"),
      t("failure.reportingDate"),
      t("failure.deadline"),
      t("failure.recommendedFixingDays"),
      t("failure.status"),
      ...(hasClaim([Claims.Failure.Read, Claims.Failure.ReadOwn])
        ? [t("failure.contractor"), t("failure.repairmens")]
        : []),
      t("failure.closedDate"),
      t("failure.createdByFamilyname"),
      t("failure.createdByForename"),
      t("failure.creatorType"),
      ...(isContractorAcceptOfTaskAssignment
        ? [t("failure.acceptedByContractor")]
        : []),
      ...(isFailureListParam1ShowInMasterView
        ? [failureListParam1Label ?? ""]
        : []),
      ...(isFailureListParam2ShowInMasterView
        ? [failureListParam2Label ?? ""]
        : []),
    ],
    [
      hasClaim,
      isContractorAcceptOfTaskAssignment,
      isMaintainedDevicesEnabled,
      isFailureListParam1ShowInMasterView,
      isFailureListParam2ShowInMasterView,
      failureListParam1Label,
      failureListParam2Label,
      t,
    ]
  );

  const exportColumnTitles = useMemo(
    () => [
      {
        displayName: t("failure.id"),
        propertyName: "identifier",
      },

      ...(isMaintainedDevicesEnabled
        ? [
            {
              displayName: t("failure.device"),
              propertyName: "deviceName",
            },
          ]
        : [
            {
              displayName: t("failure.zip"),
              propertyName: "zip",
            },
            {
              displayName: t("failure.town"),
              propertyName: "city",
            },
            {
              displayName: t("failure.street"),
              propertyName: "street",
            },
            {
              displayName: t("failure.houseNumber"),
              propertyName: "number",
            },
          ]),
      {
        displayName: t("failure.type"),
        propertyName: "typeName",
      },
      {
        displayName: t("failure.reportingDate"),
        propertyName: "reportedAt",
      },
      {
        displayName: t("failure.deadline"),
        propertyName: "deadline",
      },
      {
        displayName: t("failure.recommendedFixingDays"),
        propertyName: "optimalDeadlineDaysName",
      },
      {
        displayName: t("failure.status"),
        propertyName: "status",
      },

      ...(hasClaim([Claims.Failure.Read, Claims.Failure.ReadOwn])
        ? [
            {
              displayName: t("failure.contractor"),
              propertyName: "contractorName",
            },
            {
              displayName: t("failure.repairmens"),
              propertyName: "repairmen",
            },
          ]
        : []),
      {
        displayName: t("failure.closedDate"),
        propertyName: "closedAt",
      },
      {
        displayName: t("failure.createdByFamilyname"),
        propertyName: "creatorFamilyname",
      },
      {
        displayName: t("failure.createdByForename"),
        propertyName: "creatorForename",
      },
      {
        displayName: t("failure.comment"),
        propertyName: "comment",
      },
      {
        displayName: t("failure.creatorType"),
        propertyName: "creatorType",
      },
      ...(hasClaim([Claims.Failure.ExportNotifierData])
        ? [
            {
              displayName: t("failure.notifierFamilyname"),
              propertyName: "notifierFamilyname",
            },
            {
              displayName: t("failure.notifierForename"),
              propertyName: "notifierForename",
            },
            {
              displayName: t("failure.notifierPhone"),
              propertyName: "notifierPhone",
            },
            {
              displayName: t("failure.notifierEmail"),
              propertyName: "notifierEmail",
            },
          ]
        : []),
      ...(isContractorAcceptOfTaskAssignment
        ? [
            {
              displayName: t("failure.acceptedByContractor"),
              propertyName: "acceptedByContractor",
            },
          ]
        : []),
      ...(isFailureListParam1ShowInMasterView
        ? [
            {
              displayName: failureListParam1Label ?? "",
              propertyName: "expanseName",
            },
          ]
        : []),
      ...(isFailureListParam2ShowInMasterView
        ? [
            {
              displayName: failureListParam2Label ?? "",
              propertyName: "natureName",
            },
          ]
        : []),
    ],
    [
      hasClaim,
      isContractorAcceptOfTaskAssignment,
      isMaintainedDevicesEnabled,
      isFailureListParam1ShowInMasterView,
      isFailureListParam2ShowInMasterView,
      failureListParam1Label,
      failureListParam2Label,
      t,
    ]
  );

  return {
    columnTitles,
    exportColumnTitles,
  };
};

export default useFailuresListColumnTitles;
